import { trackEvent } from '@lib/analytics'
import type { ChatDiscussionKind } from '@orbit/api-types'
import { useEffect, useMemo, useState } from 'react'
import * as chatApi from './chatApi'
import { applyNewestPage, applyOlderPage, hasOlder, layoutStream, type ChatCard } from './core'
import { useChatUpdates } from './useChatUpdates'
import { useStreamActions } from './useStreamActions'

const OLDER_RETRY_DELAY_MS = 3000

/** The kinds with a Discussion: every taggable kind but a member or a pay-up (#276). */
const isDiscussionKind = (kind: ChatCard['kind']): kind is ChatDiscussionKind =>
	kind === 'FlightGroup' || kind === 'Accommodation' || kind === 'TripEvent' || kind === 'Idea' || kind === 'Expense'

/**
 * Drives a resource's Discussion on its detail surface: every Message tagging it,
 * directly or through a reply, paged like the Chat and kept live from the trip room.
 * Messages about it are written in the Chat. Ported from iOS `ChatDiscussionViewModel`.
 * The first load records `discussion_viewed`.
 */
export const useDiscussion = (tripId: string, resource: ChatCard, myUserId: number | undefined) => {
	const { stream, streamRef, update, actions } = useStreamActions(tripId)
	const [status, setStatus] = useState({ hasLoaded: false, loadFailed: false })
	const kind = resource.kind
	const targetId = resource.id

	const controller = useMemo(() => {
		let hasLoaded = false
		let isLoadingOlder = false
		let hasRecordedView = false

		const load = () => {
			if (!isDiscussionKind(kind)) return
			if (!hasRecordedView) {
				hasRecordedView = true
				trackEvent('discussion_viewed', { kind, tripId: Number(tripId) })
			}
			chatApi.fetchDiscussion(tripId, kind, targetId).then(
				page => {
					update(current => applyNewestPage(current, page.messages, page.nextBefore))
					hasLoaded = true
					setStatus({ hasLoaded: true, loadFailed: false })
				},
				() => setStatus(prev => ({ ...prev, loadFailed: !hasLoaded })),
			)
		}

		return {
			load,
			refresh: () => {
				if (hasLoaded) load()
			},
			loadOlder: () => {
				const cursor = streamRef.current.olderCursor
				if (!isDiscussionKind(kind) || cursor === null || isLoadingOlder) return
				isLoadingOlder = true
				chatApi.fetchDiscussion(tripId, kind, targetId, cursor).then(
					page => {
						isLoadingOlder = false
						update(current => applyOlderPage(current, page.messages, page.nextBefore, cursor))
					},
					() => {
						// Hold off a moment, so repeated clicks don't each ask again.
						setTimeout(() => {
							isLoadingOlder = false
						}, OLDER_RETRY_DELAY_MS)
					},
				)
			},
		}
	}, [kind, streamRef, targetId, tripId, update])

	useEffect(() => controller.load(), [controller])
	useChatUpdates(tripId, controller.refresh)

	const items = useMemo(() => (myUserId === undefined ? [] : layoutStream(stream, myUserId)), [stream, myUserId])

	/** What the Discussion says in place of Messages; null while it has some or is loading. */
	const placeholder =
		stream.messages.length > 0
			? null
			: status.loadFailed
				? "Couldn't load the discussion. Check your connection and try again."
				: status.hasLoaded
					? 'No messages about this yet.'
					: null

	return { items, hasOlder: hasOlder(stream), placeholder, loadOlder: controller.loadOlder, actions }
}
